/**
 * Input/response contracts for the sendAgendaEmail callable.
 *
 * Sends personalized agenda emails with ICS calendar attachments.
 * Supports sending to all event members, public-only members, or a
 * specific user (for resends / late additions).
 */

import { z } from 'zod';

export const sendAgendaEmailInputSchema = z
  .object({
    eventId: z.string().min(1),
    /**
     * Recipient selector:
     * - `'all'` — every active event member
     * - `'public'` — exclude members whose only visible sessions are internal-only types
     * - `'role:<role>'` — every active member with the given per-event role
     *   (e.g. `'role:invitee'`, `'role:internal'`, `'role:companion'`)
     * - any other string — a specific userId (single-user send / resend)
     *
     * Each recipient always gets their OWN personal agenda computed from their
     * role + assignments; the target only controls recipient selection.
     */
    target: z.string().min(1),
  })
  .strict();

export type SendAgendaEmailInput = z.infer<typeof sendAgendaEmailInputSchema>;

export interface SendAgendaEmailResponse {
  success: true;
  recipientCount: number;
}
